// src/utils/auth.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ref } from 'vue';
import router from '../router';

export const isLoggedIn = ref(!!localStorage.getItem('access_token'));

export function login(auth_data) {
  localStorage.setItem('access_token', auth_data.token);
  localStorage.setItem('refresh_token', auth_data.refresh);
  isLoggedIn.value = true;
}

export function logout() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  isLoggedIn.value = false;
  router.push('/login');
}

let refreshingToken = false;
let refreshSubscribers = [];

function isTokenExpired(token) {
  if (!token || token.split('.').length !== 3) {
    console.warn("Invalid token format detected.");
    return true; // Treat missing or invalid tokens as expired
  }

  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch (error) {
    console.error("Token decoding failed:", error);
    return true; // Consider token expired if decoding fails
  }
}

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newAccessToken) {
  refreshSubscribers.forEach((cb) => cb(newAccessToken));
  refreshSubscribers = [];
}

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


axios.interceptors.response.use(
  (response) => response,
    async (error) => {
    const originalRequest = error.config;

    // logout if url is token refresh url and 401
    const isRefreshEndpoint = error.config.url?.endsWith('/token/refresh/');
    if (error.response && error.response.status === 401 && isRefreshEndpoint) {
      refreshingToken = false;
      logout();
    }

    if (error.response && error.response.status === 401 && error.response.data.code === 'token_not_valid') {
      const refreshToken = localStorage.getItem('refresh_token');
      const accessToken = localStorage.getItem('access_token');

      if (refreshToken && !refreshingToken && isTokenExpired(accessToken)) {
        refreshingToken = true;

        try {
          const response = await axios.post('/api/v1/token/refresh/', { refresh: refreshToken });
          const { access: newAccessToken, refresh: newRefresh } = response.data;

          localStorage.setItem('access_token', newAccessToken);

          if (newRefresh) {
            localStorage.setItem('refresh_token', newRefresh);
          }

          isLoggedIn.value = true;
          refreshingToken = false;
          onRefreshed(newAccessToken);

          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axios(originalRequest);

        } catch (refreshError) {
          refreshingToken = false;
          logout();
          return Promise.reject(refreshError);
        }
      } else if (refreshingToken) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newAccessToken) => {
            if (newAccessToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              resolve(axios(originalRequest));
            } else {
              logout();
              reject(new Error("Token refresh failed"));
            }
          });
        });
      } else {
        logout();
      }
    }

    return Promise.reject(error);
  }
);
